import { type Doc } from "building-types/doc";
import { docEntityTypes } from "building-types/doc/docEntityTypes";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { PlanarMeasurementType } from "building-types/entities/measurementTypes";
import type { DocEntityHandle, OptionHandle } from "building-types/handles/docEntityHandleTypes";
import { SettingsType } from "building-types/settings/settingsType";
import { Lazy } from "common/data/lazy";
import { docForEachCatalogItem } from "../docGetCatalogItem";
import type { DocEntity } from "./docEntity";
import { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import docGetEntity from "./docGetEntity";

const docEntityTypesWithoutCatalogItem = new Lazy<DocEntityType[]>(() =>
  docEntityTypes.filter((type) => type !== "CATALOG_ITEM"),
);

function handleTypesForEntityIteration(handleTypes: DocEntityType[]): DocEntityType[] {
  if (!handleTypes.includes("CATALOG_ITEM")) {
    return handleTypes;
  }

  if (handleTypes === docEntityTypes) {
    return docEntityTypesWithoutCatalogItem.value;
  }

  return handleTypes.filter((type) => type !== "CATALOG_ITEM");
}

/**
 * Used to iterate through all entities in the Doc. This is very expensive if the handleTypes param is not
 * supplied and narrowed so be careful when using this.
 *
 * @param doc The doc to iterate
 * @param inputCallback The callback to invoke with each entity and its handle as argument
 * @param includeGroups Whether to include GROUP entities
 * @param handleTypes An array of handle types to include. If not provided, all handle types are included (verbose)
 */
export default function docForEachEntity(
  doc: Doc,
  callback: (entity: DocEntity, handle: DocEntityHandle) => void,
  includeGroups = true,
  handleTypes = docEntityTypes,
): void {
  if (handleTypes.includes("CATALOG_ITEM")) {
    docForEachCatalogItem(doc, (handle, item) => {
      callback(item, handle);
    });
  }

  docForEachEntityHandle(
    doc,
    (handle) => {
      const entity = docGetEntity(doc, handle);
      if (entity) {
        callback(entity, handle);
      }
    },
    includeGroups,
    handleTypesForEntityIteration(handleTypes),
  );
}

export function docForEachEntityHandle(
  doc: Doc,
  callback: (handle: DocEntityHandle) => void,
  includeGroups = true,
  handleTypes = docEntityTypes,
) {
  for (const type of handleTypes) {
    if (!includeGroups && type === "GROUP") {
      continue;
    }

    docForEachEntityHandleOfType(doc, type as DocEntityType, callback);
  }
}

export function docForEachEntityOfType(
  doc: Doc,
  entityType: DocEntityType,
  callback: (entity: DocEntity, handle: DocEntityHandle) => void,
) {
  if (entityType === "CATALOG_ITEM") {
    docForEachCatalogItem(doc, (handle, item) => {
      callback(item, handle);
    });
    return;
  }

  docForEachEntityHandleOfType(doc, entityType, (handle) => {
    const entity = docGetEntity(doc, handle);
    if (entity) {
      callback(entity, handle);
    }
  });
}

export function docForEachEntityHandleOfType<T extends DocEntityType>(
  doc: Doc,
  entityType: T,
  callback: (handle: DocEntityHandle) => void,
) {
  switch (entityType) {
    case "ELECTRICAL_RULE": {
      for (const settingsId in doc.settings) {
        const settings = doc.settings[settingsId];
        if (settings.type !== SettingsType.ELECTRICAL_SETTINGS) {
          continue;
        }

        for (const ruleId in settings.rules) {
          callback({
            type: "ELECTRICAL_RULE",
            settingsHandle: { type: "SETTINGS", id: settingsId },
            id: ruleId,
          });
        }
      }

      break;
    }
    case "SHAPE": {
      // TODO this looks incorrect
      for (const measurementId in doc.measurements) {
        const measurement = doc.measurements[measurementId];
        if (measurement.measurementType === PlanarMeasurementType.PLANAR_AREA) {
          for (const shapeId in measurement.shapes) {
            callback({ type: "SHAPE", parent: { type: "MEASUREMENT", id: measurementId }, shapeId });
          }
        }
      }

      break;
    }
    case "CATALOG_ITEM": {
      docForEachCatalogItem(doc, callback);
      break;
    }
    case "OPTION": {
      for (const optionSetId in doc.optionSets) {
        const optionSet = doc.optionSets[optionSetId];

        for (const optionKey in optionSet.options) {
          callback({ type: "OPTION", optionSetId, optionKey } as OptionHandle);
        }
      }
      break;
    }
    // Edge is a special case in that it should be selectable, but is also an
    // ephemeral entity similar to the below entity types
    case "EDGE": {
      for (const roomId in doc.rooms) {
        const room = doc.rooms[roomId];
        if (!room) {
          continue;
          /**
           * @NOTE - there is a bug here - somehow we are getting passed
           * a doc with the format of
           * {
           *  rooms : {
           *      [undefined] : undefined.
           *    }
           * }
           *
           * this is likely due to docReffing and needs to be
           * revisited. For the time being we can ignore these,
           * as that room is removed elsewhere in the pipeline
           */
        }
        const roomEdgeIds = room?.edgeIds;
        if (roomEdgeIds) {
          for (let edgeIndex = 0; edgeIndex < roomEdgeIds.length; edgeIndex++) {
            callback({
              type: "EDGE",
              parent: { type: "ROOM", id: room.id },
              shapeId: "outline",
              edgeId: edgeIndex,
            });
          }
        }
      }
      break;
    }
    case "PROP": {
      for (const id in doc.propAttributes) {
        callback({ type: "PROP", id });
      }
      break;
    }
    case "AUTO_DIMENSION":
    case "GENERATED_ENTITY":
    case "EDIT_MODE":
    case "FLOOR_BAY":
    case "FLOOR_PERIMETER_OPENING":
    case "ROOF_SIDE":
    case "FLOOR_SPLIT_LINE":
    case "VERTEX":
    case "SYSTEM_PARAMETER":
    case "INDETERMINATE_ENTITY_TYPE":
      break;

    default: {
      const docField = docEntityTypeToDocTableName[entityType];
      if (!docField) {
        console.warn(`Unable to find doc field for entity type ${entityType}`);
        break;
      }

      for (const id in doc[docField]) {
        const handle = { type: entityType, id };
        callback(handle as DocEntityHandle);
      }
    }
  }
}
